/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/routeHelpers.ts (ARS - Route Selections)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing ARS routes in forms.

────────────────────────────────────────────────────────────────*/

import { RouteFormState, FormState } from '../types';
import { applySequentialPriorities, createEmptyRoute } from './formDefaults';

/**
 * Serialize form state for API submission
 */
export function serializeFormState(state: FormState, includeRouteIds: boolean) {
  return {
    name: state.name.trim(),
    description: state.description.trim(),
    enabled: state.enabled,
    routes: applySequentialPriorities(state.routes).map((route) => ({
      ...(includeRouteIds && route.id ? { id: route.id } : {}),
      outboundRouteId: route.outboundRouteId,
      timeGroupId: route.timeGroupId,
      priority: route.priority,
      enabled: route.enabled
    }))
  };
}

/**
 * Clone form state
 */
export function cloneFormState(state: FormState): FormState {
  return {
    ...state,
    routes: state.routes.map((route) => ({ ...route }))
  };
}

/**
 * Add route to form state
 */
export function addRouteToFormState(state: FormState): FormState {
  return {
    ...state,
    routes: applySequentialPriorities([
      ...state.routes,
      createEmptyRoute(state.routes.length + 1)
    ])
  };
}

/**
 * Remove route from form state
 */
export function removeRouteFromFormState(state: FormState, index: number): FormState {
  return {
    ...state,
    routes: applySequentialPriorities(state.routes.filter((_, i) => i !== index))
  };
}

/**
 * Move route in form state
 */
export function moveRouteInFormState(state: FormState, index: number, direction: number): FormState {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= state.routes.length) {
    return state;
  }
  const nextRoutes = [...state.routes];
  const [current] = nextRoutes.splice(index, 1);
  nextRoutes.splice(nextIndex, 0, current);
  return {
    ...state,
    routes: applySequentialPriorities(nextRoutes)
  };
}

/**
 * Update route in form state
 */
export function updateRouteInFormState<K extends keyof RouteFormState>(
  state: FormState,
  index: number,
  field: K,
  value: RouteFormState[K]
): FormState {
  return {
    ...state,
    routes: applySequentialPriorities(
      state.routes.map((route, routeIndex) =>
        routeIndex === index ? { ...route, [field]: value } : route
      )
    )
  };
}

